import { useEffect, useRef, useState } from 'react';

/**
 * 숫자 카운트업 애니메이션 (requestAnimationFrame)
 * 웹뷰 환경에서 setInterval보다 부드럽고 백그라운드에서 자동 일시정지됨
 *
 * @param {number} end           목표값
 * @param {object} opts
 * @param {number} opts.duration ms (default 1200)
 * @param {number} opts.start    시작값 (default 0)
 * @param {boolean} opts.enabled 애니메이션 활성화 여부 (in-view 트리거용)
 * @returns {number} 현재 보간된 값
 */
export const useCountUp = (end, { duration = 1200, start = 0, enabled = true } = {}) => {
    const [value, setValue] = useState(enabled ? start : end);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!enabled) return;

        startTimeRef.current = null;

        const tick = (timestamp) => {
            if (startTimeRef.current === null) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const t = Math.min(elapsed / duration, 1);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(start + (end - start) * eased);

            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setValue(end);
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [end, start, duration, enabled]);

    return value;
};